import React from "react";
import Spinner from "@atlas/uicorn/lib/Display/Spinner";
import Text from "@atlas/uicorn/lib/Display/Text";
import styled from "styled-components";
import AttachmentPreview from "./attachment-preview";
import { TAttachment } from "@atlas/tool/box/file/types";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";
import CrossIcon from "@atlas/uicorn/lib/Icon/CrossIcon";
import Link from "next/link";
import useRefCallback from "use-ref-callback";

const ComposerAttachments = React.memo(function ComposerAttachments(
  props: TComposerAttachmentsProps
): React.ReactElement | null {
  const { attachments, disabled, uploading, onRemove, ...elementProps } = props;

  const handleRemove = useRefCallback((event, attachment) => {
    event.preventDefault();
    onRemove && onRemove(attachment);
  });

  if (!attachments.length && !uploading) return null;

  return (
    <SNewComposerAttachments {...elementProps}>
      {attachments.map((a) => (
        <SAttachment key={a.url} href={a.url}>
          <AttachmentPreview key={a.url} attachment={a} />
          <SRemoveButton onClick={(event) => handleRemove(event, a)} />
        </SAttachment>
      ))}
      {uploading && uploading > 0 ? (
        <SUploadingStatus>
          <Spinner />
          <Text>
            {uploading} {uploading > 1 ? "files are" : "file is"} uploading
          </Text>
        </SUploadingStatus>
      ) : null}
    </SNewComposerAttachments>
  );
});

export default ComposerAttachments;

const SRemoveButton = styled(IconButton).attrs({
  icon: <CrossIcon size={10} />,
  tooltip: "Remove",
})`
  --space: 4px;
  position: absolute;
  top: 0;
  right: 0;
  background: ${(p) => p.theme.color.grey0};
  border-radius: 50%;
  box-shadow: ${(p) => p.theme.shadow.extraLow};
  translate: var(--space) calc(-1 * var(--space));
  padding: var(--space);

  &:disabled {
    opacity: 0;
    scale: 0;
  }

  &:not(:disabled):active {
    box-shadow: 0 0 0 1px #26262624;
  }

  svg {
    vertical-align: middle;
  }
`;

const SAttachment = styled(Link).attrs({ target: "_blank" })`
  position: relative;

  & > div {
    min-width: 46px;
    height: 46px;
  }

  & img {
    border: 1px solid ${(p) => p.theme.color.grey6};
    border-radius: 8px;
  }
`;

const SUploadingStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SNewComposerAttachments = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 24px;
  overflow: auto;
  padding: 8px 8px 0 0;
`;

export type TComposerAttachmentsProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  attachments: TAttachment[];
  disabled?: boolean;
  onRemove?: (attachment: TAttachment) => void;
  uploading?: number;
};
